import { useCallback, useEffect, useRef, useState } from "react";

import axiosInstance from "../../utils/axiosInstance";
import {
  readLifecycleSnapshot,
  REVIEW_LIFECYCLE_PARAMS,
  REVIEW_LIFECYCLE_PATH,
  shouldAcceptLifecycleSnapshot,
} from "./reviewLifecycle";

export const useReviewLifecycle = ({
  enabled,
  expectedLevel,
  sourceGeneratedAt = null,
}) => {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState("idle");
  const requestRef = useRef(0);
  const controllerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const generation = ++requestRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus((value) => (value === "ready" ? value : "loading"));
    try {
      const response = await axiosInstance(
        REVIEW_LIFECYCLE_PATH,
        "get",
        undefined,
        { params: REVIEW_LIFECYCLE_PARAMS, signal: controller.signal },
      );
      if (requestRef.current !== generation || controller.signal.aborted) {
        return;
      }
      const incoming = readLifecycleSnapshot(response?.data, expectedLevel);
      setSnapshot((current) =>
        shouldAcceptLifecycleSnapshot(current, incoming) ? incoming : current,
      );
      setStatus(incoming ? "ready" : "error");
    } catch (error) {
      if (requestRef.current !== generation || controller.signal.aborted) {
        return;
      }
      setStatus("error");
    }
  }, [enabled, expectedLevel]);

  useEffect(() => {
    if (!enabled) {
      requestRef.current += 1;
      controllerRef.current?.abort();
      setSnapshot(null);
      setStatus("idle");
      return undefined;
    }
    refresh();
    return () => controllerRef.current?.abort();
  }, [enabled, refresh, sourceGeneratedAt]);

  return { snapshot, status, refresh };
};

export default useReviewLifecycle;
